import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { startCase } from "lodash";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import {
  useGetAllRelationships,
  useGetCashWithdrawable,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import type { AccountSubType } from "~/v2/api/rest/account";
import { createAchTransfer } from "~/v2/api/rest/banking";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { postWithdrawSurvey } from "../../common/util";
import type { FromToItem } from "../../transferForm/FromTo";
import {
  getCorrectAmount,
  getSubmittableWithholdingPct,
  type TransferSchema,
  withdrawableSuperRefinement,
  zIraDistribution,
  zTransferSchema,
} from "../../transferForm/schema";
import {
  getEligibleDistributionReasons,
  getIsAtLeast59Half,
} from "../ira/distributionReasons";

export const useAchWithdrawController = () => {
  const history = useHistory();

  const [isSubmitChecked, setIsSubmitChecked] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const { ach } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  // Find the first active (non-canceled, non-rejected) relationship for actual use
  const achRelationship = ach.relationships.find(
    (rel) => rel.status !== "CANCELED" && rel.status !== "REJECTED",
  );
  const hasACHRelationship = !!achRelationship;

  const { data: withdrawableData } = useGetCashWithdrawable({
    accountID: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });
  const withdrawable = withdrawableData ?? 0;

  const isIRA = currentAccount?.account_type === "ira";
  const isOver59Half = getIsAtLeast59Half(details?.date_of_birth);
  const eligibleReasons = getEligibleDistributionReasons({
    accountSubType: currentAccount?.account_sub_type as
      | AccountSubType
      | undefined,
    isOver59Half,
  });

  const form = useForm<TransferSchema>({
    resolver: zodResolver(
      zTransferSchema.superRefine((fields, refinementContext) => {
        withdrawableSuperRefinement({
          fields,
          refinementContext,
          withdrawable,
        });
      }),
    ),
    // Seeding the `ira` object for IRA accounts is what turns on the nested
    // distribution validation; non-IRA withdrawals leave it undefined.
    defaultValues: { ira: isIRA ? {} : undefined },
    mode: "onChange",
  });

  const amountString = form.watch("amount");
  const amount = getCorrectAmount(amountString);

  const reason = form.watch("reason");
  const reasonDetails = form.watch("reason_details");
  const ira = form.watch("ira");

  // For IRA withdrawals, keep Confirm disabled until the distribution fields are
  // valid (reason selected + withholding within bounds) so the user can't reach
  // the confirmation step with incomplete or invalid data. Non-IRA withdrawals
  // have no `ira` object and are unaffected.
  const isIraValid = !isIRA || zIraDistribution.safeParse(ira ?? {}).success;
  const isConfirmDisabled = amount <= 0 || amount > withdrawable || !isIraValid;

  const onBackClicked = () => {
    history.push(Path.ROUTE_FUNDING_WITHDRAW);
  };

  const withdraw = useMutationRequest(
    () => {
      setIsSubmitEnabled(false);

      const fedPct = getSubmittableWithholdingPct(ira?.fed_pct);
      const statePct = getSubmittableWithholdingPct(ira?.state_pct);

      return createAchTransfer(currentAccount?.id || "", {
        transfer_type: "ach",
        direction: "OUTGOING",
        relationship_id: achRelationship?.id || "",
        amount: String(amount),
        ...(isIRA && {
          ira: {
            distribution_reason: ira?.distribution_reason,
            tax_withholding: {
              ...(fedPct && { fed_pct: fedPct }),
              ...(statePct && { state_pct: statePct }),
            },
          },
        }),
      });
    },
    {
      onSuccess: () => {
        toastSuccess("Transfer created");

        if (reason) {
          postWithdrawSurvey(currentAccount?.id || "", {
            reason,
            reasonDetails,
          });
        }

        setIsSubmitEnabled(true);
        history.push(Path.ROUTE_FUNDING_HISTORY);
      },
    },
  );

  const onSubmitValid: SubmitHandler<TransferSchema> = ({ amount }) => {
    withdraw.mutate(getCorrectAmount(amount));
  };

  const onSubmitInvalid: SubmitErrorHandler<TransferSchema> = (errors) => {
    Object.entries(errors)
      .filter(([_key, error]) => !!error)
      .forEach(([key, error]) => {
        toastError(`${startCase(key)} is invalid: ${error.message}`);
      });
  };

  const alpacaAccountInfo: FromToItem = {
    imgSrc: "resources/images/logo-collapsed.svg",
    name: "Alpaca Account",
    account: currentAccount?.account_number ?? "",
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: withdrawable is the trigger: the amount field is re-validated when the withdrawable balance arrives. The body writes rather than reads it.
  useEffect(() => {
    form.reset();
  }, [withdrawable, form.reset]);

  const isLoading = !currentAccount || !details || ach.isLoading;

  return {
    isLoading,
    hasACHRelationship,
    achRelationship,
    alpacaAccountInfo,
    form,
    isIRA,
    eligibleReasons,
    isSubmitChecked,
    setIsSubmitChecked,
    isSubmitEnabled,
    isConfirmDisabled,
    amountString,
    withdrawable,
    onBack: onBackClicked,
    onSubmit: form.handleSubmit(onSubmitValid, onSubmitInvalid),
  };
};
